import React, { useState, useEffect, useCallback } from "react";
import {
  FaTimes, FaHashtag, FaCube, FaKey, FaCalculator,
  FaCheckCircle, FaTimesCircle, FaArrowLeft, FaArrowRight, FaRegCopy,
} from "react-icons/fa";
import { CustomDropdown, NumberInput } from "../../atoms";
import { Stepper } from "../../shared";
import useAuth from "../../../Hooks/useAuth";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";
import { useUploadManualQRMutation, useUploadManualQuotaMutation } from "../../../store/storageRoles/devicesSharedApi";

/**
 * Cihaz manuel kota drawer — adım adım (wizard) akış.
 *
 *   1. Ürün      — cihazdaki ürünlerden seçim + mevcut kota bilgisi
 *   2. Makine    — makine ekranından okunan sayaç + kota
 *   3. Kod       — makinedeki kod → doğrulama (uploadManualQR)
 *   4. Yeni Kota — makinenin olması gereken kota → kod üretimi (uploadManualQuota)
 *   5. Sonuç     — üretilen kod + tüm bilgilerin toplu özeti
 *
 * Her adımda yalnızca o adımın bilgisi istenir; ileri gitmeden önce doğrulanır.
 * Kod doğrulandıktan (adım 3 tamamlandıktan) sonra geri dönülemez — HMAC
 * sözleşmesi girilen değerlere bağlı, değişirse üretilen kod tutmaz.
 *
 * Not: Drawer kabuğu ortak `shared/Drawer` DEĞİL — bu akışta backdrop/ESC ile
 * kapanma bilinçli olarak engellenir (yarım kalan kota işlemi cihazı bozar).
 */

// Adım etiketleri makine ekranındaki terimlerle aynı tutulur (tablet:
// "Toplam Sayaç", "Kalan Kota", "Güvenlik Kodu", "Kod Girin") — operatör iki
// ekran arasında aynı kelimeyi görsün.
const STEPS = ["Ürün", "Değerler", "Güvenlik Kodu", "Yeni Kota", "Sonuç"];
const STEP_PRODUCT = 1;
const STEP_MACHINE = 2;
const STEP_CODE = 3;
const STEP_NEW_QUOTA = 4;
const STEP_RESULT = 5;

/** Adım başlığı + açıklaması — her adımın ne istediğini net söyler. */
const StepIntro = ({ title, description }) => (
  <div>
    <h4 className="text-base font-SemiBold text-gray-800">{title}</h4>
    <p className="text-sm text-gray-500 mt-1">{description}</p>
  </div>
);

/** Özet satırı — sonuç adımındaki toplu bilgi listesi için. */
const SummaryRow = ({ label, value, highlight = false }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0">
    <span className="text-sm text-gray-500 shrink-0">{label}</span>
    <span
      className={`text-sm text-right break-all ${
        highlight ? "font-Bold text-violet-700" : "font-SemiBold text-gray-800"
      }`}
    >
      {value}
    </span>
  </div>
);

/** Makine ↔ panel karşılaştırma satırı — iki değer yan yana, fark görünür. */
const CompareRow = ({ label, machine, panel }) => {
  const diff = machine !== panel;
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 py-2 border-b border-gray-100 last:border-b-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`w-16 text-right text-sm font-SemiBold ${diff ? "text-amber-600" : "text-gray-800"}`}>
        {machine}
      </span>
      <span className="w-16 text-right text-sm font-SemiBold text-gray-500">{panel}</span>
    </div>
  );
};

const ManualQuotaDrawer = ({ open, onClose, device, onSuccess }) => {
  const { auth } = useAuth();
  const token = auth?.accessToken;

  // Giriş/çıkış kayması + kapanış boyunca son cihazın dondurulması.
  // (Çağıran taraf onClose'da device'ı null'a çekiyor; donmazsa drawer hâlâ
  // ekrandayken içerik boşalıyor.)
  const { mounted, entered, data: shownDevice } = useDrawerTransition(open, device);

  const [step, setStep] = useState(STEP_PRODUCT);
  const [code, setCode] = useState("");
  const [productName, setProductName] = useState("");
  const [productQuota, setProductQuota] = useState("");
  const [productCounter, setProductCounter] = useState("");
  const [newQuota, setNewQuota] = useState(0);
  const [errors, setErrors] = useState({});
  const [generatedCode, setGeneratedCode] = useState(null);
  const [copied, setCopied] = useState(false);

  // Yalnız merkez — bayi manuel kota yükleyemez (#178).
  const [uploadManualQR, {
    data: qrData, isLoading: qrLoading, isSuccess: qrSuccess,
    isError: qrError, error: qrErrorData, reset: resetQR,
  }] = useUploadManualQRMutation();

  const [uploadManualQuota, {
    data: quotaData, isLoading: quotaLoading, isSuccess: quotaSuccess,
    isError: quotaError, error: quotaErrorData, reset: resetQuota,
  }] = useUploadManualQuotaMutation();

  const isLoading = qrLoading || quotaLoading;
  const serialNo = shownDevice?.serialNo ?? "";
  const isFinished = !!generatedCode;

  const productOptions = (shownDevice?.productInfo?.productInfo ?? []).map((p) => ({
    id: p.productName,
    label: p.productName,
    value: p.productName,
  }));

  // Panelde kayıtlı (sunucudaki) değerler — makinedeki değerlerle karşılaştırma
  // için her adımda referans olarak gösterilir.
  const selectedProduct = shownDevice?.productInfo?.productInfo?.find((p) => p.productName === productName);
  const panelQuota = selectedProduct?.quota ?? 0;
  const panelCounter = selectedProduct?.counter ?? 0;

  // Doğrulama sonrası sunucudan dönen sistem (panel) tarafı değerleri.
  const systemInfo = qrData?.data?.targetProductInfo;
  const resultInfo = quotaData?.data;

  // Sıfırlama `open` yerine `mounted`'a bağlı: kapanış kayması bitmeden
  // adım 1'e dönerse kullanıcı drawer hâlâ ekrandayken sıfırlanmayı görüyor.
  useEffect(() => {
    if (!mounted) {
      setStep(STEP_PRODUCT);
      setCode("");
      setProductName("");
      setProductQuota("");
      setProductCounter("");
      setNewQuota(0);
      setErrors({});
      setGeneratedCode(null);
      setCopied(false);
      resetQR();
      resetQuota();
    }
  }, [mounted, resetQR, resetQuota]);

  // Kod doğrulandı → yeni kota adımına geç.
  useEffect(() => {
    if (qrSuccess) setStep((s) => (s === STEP_CODE ? STEP_NEW_QUOTA : s));
  }, [qrSuccess]);

  // Kod üretildi → sonuç adımı (büyük kod + toplu özet).
  useEffect(() => {
    if (quotaSuccess && quotaData?.data?.encryptedKey) {
      setGeneratedCode(quotaData.data.encryptedKey);
      setStep(STEP_RESULT);
      onSuccess?.();
    }
  }, [quotaSuccess, quotaData, onSuccess]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [open]);

  /** Adım bazlı doğrulama — yalnızca o adımın alanlarına bakar. */
  const validateStep = useCallback(
    (target) => {
      const t = {};
      if (target === STEP_PRODUCT && !productName) t.productName = "Ürün seçin.";
      if (target === STEP_MACHINE) {
        if (!productCounter || Number(productCounter) < 0) t.productCounter = "Sayaç girin.";
        // Makinedeki mevcut kota negatif olabilir (cihaz fazla harcayıp -3'e
        // düşebilir); HMAC kodun tutması için gerçek değer girilebilmeli.
        if (productQuota === "" || Number.isNaN(Number(productQuota))) t.productQuota = "Kota (makinedeki) girin.";
      }
      if (target === STEP_CODE && !code.trim()) t.code = "Kod girin.";
      setErrors(t);
      return Object.keys(t).length === 0;
    },
    [productName, productCounter, productQuota, code]
  );

  const handleNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    setErrors({});
    setStep((s) => Math.max(STEP_PRODUCT, s - 1));
  };

  const handleKoduDogrula = (e) => {
    e.preventDefault();
    if (!validateStep(STEP_CODE) || !token || !serialNo) return;
    uploadManualQR({
      token,
      code: code.trim(),
      serialNo,
      productName,
      productQuota: Number(productQuota),
      productCounter: Number(productCounter),
    });
  };

  const handleKodUret = (e) => {
    e.preventDefault();
    if (!qrSuccess || newQuota < 0 || !token) return;
    uploadManualQuota({
      token,
      code: code.trim(),
      serialNo,
      productName,
      productQuota: Number(productQuota),
      productCounter: Number(productCounter),
      newQuota: Number(newQuota),
    });
  };

  const handleProductChange = (option) => {
    setProductName(option?.value || "");
    setErrors((p) => ({ ...p, productName: undefined }));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(generatedCode));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const handleBitti = () => {
    setGeneratedCode(null);
    onClose();
  };

  const fmt = (v) => (v === "" || v == null ? "-" : Number(v).toLocaleString("tr-TR"));

  if (!mounted || !shownDevice) return null;

  return (
    <>
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[460px] transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9999] flex flex-col bg-white`}
      >
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-violet-500 rounded-full animate-spin" />
            <p className="mt-4 text-sm font-Medium text-gray-700">İşleniyor...</p>
          </div>
        )}

        <div className="bg-gradient-to-r from-violet-600 to-purple-700 px-5 py-4 text-white shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <FaHashtag className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-Bold tracking-wide">Manuel Kota Yükleme</h3>
                <p className="text-xs text-white/80 mt-0.5">
                  {shownDevice.customName || shownDevice.deviceName} : {serialNo}
                </p>
              </div>
            </div>
            {/* Kod doğrulandıktan sonra X gizli — sadece "Tamamlandı" ile çıkış */}
            {!qrSuccess && !isFinished && (
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/10 hover:bg-white/20 disabled:opacity-50"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Adım göstergesi */}
        <div className="shrink-0 border-b border-gray-100 px-3 pt-4 pb-3">
          <Stepper steps={STEPS} current={step} />
        </div>

        {/* Aktif adımın gövdesi */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {step === STEP_PRODUCT && (
            <>
              <StepIntro
                title="1. Ürün seçin"
                description="Kota yüklenecek ürünü cihazdaki ürünler arasından seçin."
              />
              <CustomDropdown
                label={
                  <span className="text-base font-Medium text-gray-600 flex items-center gap-2">
                    <FaCube /> Ürün
                  </span>
                }
                options={productOptions}
                value={productName ? { id: productName, label: productName, value: productName } : null}
                onChange={handleProductChange}
                placeholder="Cihazdaki ürünlerden seçin"
                searchPlaceholder="Ürün ara..."
                error={!!errors.productName}
                errorMessage={errors.productName}
              />
              {productName && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-Bold tracking-[0.08em] text-gray-400 mb-1">
                    PANELDE KAYITLI
                  </p>
                  <SummaryRow label="Toplam Sayaç" value={fmt(panelCounter)} />
                  <SummaryRow label="Kalan Kota" value={fmt(panelQuota)} />
                </div>
              )}
            </>
          )}

          {step === STEP_MACHINE && (
            <>
              <StepIntro
                title="2. Makine değerleri"
                description='Makine ekranındaki "Manuel Kota Yükleme" bölümünde yazan Toplam Sayaç ve Kalan Kota değerlerini birebir girin.'
              />
              {/* Panel değerleri referans olarak yanında dursun — makinedeki
                  değerle farkı operatör anında görsün. */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-Bold tracking-[0.08em] text-gray-400 mb-1">
                  PANELDE KAYITLI
                </p>
                <SummaryRow label="Ürün" value={productName} />
                <SummaryRow label="Toplam Sayaç" value={fmt(panelCounter)} />
                <SummaryRow label="Kalan Kota" value={fmt(panelQuota)} />
              </div>
              <NumberInput
                label={
                  <span className="text-base font-Medium text-gray-600 flex items-center gap-2">
                    <FaCalculator /> Toplam Sayaç <span className="text-gray-400">(makinedeki)</span>
                  </span>
                }
                value={productCounter === "" ? "" : Number(productCounter)}
                onChange={(v) => { setProductCounter(String(v)); setErrors((p) => ({ ...p, productCounter: undefined })); }}
                placeholder="Makinedeki Toplam Sayaç"
                min={0}
                error={!!errors.productCounter}
                errorMessage={errors.productCounter}
              />
              <NumberInput
                label={
                  <span className="text-base font-Medium text-gray-600 flex items-center gap-2">
                    <FaHashtag /> Kalan Kota <span className="text-gray-400">(makinedeki)</span>
                  </span>
                }
                value={productQuota === "" ? "" : Number(productQuota)}
                onChange={(v) => { setProductQuota(String(v)); setErrors((p) => ({ ...p, productQuota: undefined })); }}
                placeholder="Makinedeki Kalan Kota"
                error={!!errors.productQuota}
                errorMessage={errors.productQuota}
              />
            </>
          )}

          {step === STEP_CODE && (
            <>
              <StepIntro
                title="3. Güvenlik Kodu"
                description='Makine ekranında "Güvenlik Kodu" olarak yazan kodu girip doğrulayın. Doğrulandıktan sonra önceki adımlara dönülemez.'
              />
              <div>
                <label className="text-base font-Medium text-gray-600 flex items-center gap-2 mb-1.5">
                  <FaKey className="w-4 h-4" /> Güvenlik Kodu <span className="text-gray-400">(makinedeki)</span>
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => { setCode(e.target.value); setErrors((p) => ({ ...p, code: undefined })); }}
                  placeholder="Makinedeki Güvenlik Kodu"
                  className={`w-full px-4 py-2.5 rounded-xl border-2 bg-white text-gray-900 placeholder-gray-400 outline-none ${
                    errors.code ? "border-red-300" : "border-gray-200 focus:border-violet-400"
                  }`}
                />
                {errors.code && <p className="text-xs text-red-500 mt-1">{errors.code}</p>}
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-Bold tracking-[0.08em] text-gray-400 mb-1">
                  GİRİLEN DEĞERLER
                </p>
                <SummaryRow label="Ürün" value={productName} />
                {/* Makine ↔ panel karşılaştırması: farklı olan değer amber yanar */}
                <div className="grid grid-cols-[1fr_auto_auto] gap-3 pt-2 pb-1">
                  <span />
                  <span className="w-16 text-right text-[11px] font-Bold tracking-wide text-gray-400">MAKİNE</span>
                  <span className="w-16 text-right text-[11px] font-Bold tracking-wide text-gray-400">PANEL</span>
                </div>
                <CompareRow
                  label="Toplam Sayaç"
                  machine={fmt(productCounter)}
                  panel={fmt(panelCounter)}
                />
                <CompareRow
                  label="Kalan Kota"
                  machine={fmt(productQuota)}
                  panel={fmt(panelQuota)}
                />
              </div>
            </>
          )}

          {step === STEP_NEW_QUOTA && (
            <>
              <StepIntro
                title="4. Yeni Kalan Kota"
                description="Güvenlik Kodu doğrulandı. Makinede olması gereken Kalan Kota değerini girin."
              />
              <NumberInput
                label={
                  <span className="text-base font-Medium text-gray-600 flex items-center gap-2">
                    <FaHashtag /> Yeni Kalan Kota
                  </span>
                }
                value={newQuota}
                onChange={setNewQuota}
                placeholder="Makinede olması gereken Kalan Kota"
                min={0}
              />
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-Bold tracking-[0.08em] text-gray-400 mb-1">
                  KARŞILAŞTIRMA
                </p>
                <SummaryRow label="Ürün" value={productName} />
                <SummaryRow label="Makinedeki Kalan Kota" value={fmt(productQuota)} />
                <SummaryRow label="Panelde Kalan Kota" value={fmt(systemInfo?.quota ?? panelQuota)} />
                <SummaryRow label="Panelde Toplam Sayaç" value={fmt(systemInfo?.totalCounter ?? panelCounter)} />
              </div>
            </>
          )}

          {step === STEP_RESULT && isFinished && (
            <>
              <div className="flex flex-col items-center text-center gap-3">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                  <FaCheckCircle className="w-9 h-9 text-emerald-600" />
                </div>
                <p className="text-sm font-Medium text-gray-500">Üretilen Kod</p>
                <p className="text-2xl sm:text-3xl font-Bold text-violet-700 font-mono break-all tracking-wider">
                  {generatedCode}
                </p>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
                >
                  <FaRegCopy className="w-3.5 h-3.5" />
                  {copied ? "Kopyalandı" : "Kopyala"}
                </button>
                <p className="text-sm text-gray-500 max-w-xs">
                  Bu kodu makinedeki <span className="font-SemiBold text-gray-700">"Kod Girin"</span> alanına
                  yazarak kota güncellemesini tamamlayın.
                </p>
              </div>

              {/* Toplu özet — akış boyunca girilen ve sunucudan dönen değerler */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-Bold tracking-[0.08em] text-gray-400 mb-1">
                  İŞLEM ÖZETİ
                </p>
                <SummaryRow label="Cihaz" value={shownDevice.customName || shownDevice.deviceName} />
                <SummaryRow label="Seri No" value={resultInfo?.device?.serialNo || serialNo} />
                <SummaryRow label="Ürün" value={resultInfo?.device?.type || productName} />
                <SummaryRow label="Güvenlik Kodu" value={code.trim()} />
                <SummaryRow
                  label="Makinedeki Toplam Sayaç"
                  value={fmt(resultInfo?.unConnectedDevice?.totalCounter ?? productCounter)}
                />
                <SummaryRow
                  label="Makinedeki Kalan Kota"
                  value={fmt(resultInfo?.unConnectedDevice?.quota ?? productQuota)}
                />
                <SummaryRow label="Panelde Toplam Sayaç" value={fmt(resultInfo?.targetProductInfo?.totalCounter)} />
                <SummaryRow
                  label="Yeni Kalan Kota"
                  value={fmt(resultInfo?.targetProductInfo?.quota ?? newQuota)}
                  highlight
                />
                <SummaryRow
                  label="Kota farkı"
                  value={`${Number(newQuota) - Number(productQuota) >= 0 ? "+" : ""}${fmt(
                    Number(newQuota) - Number(productQuota)
                  )}`}
                  highlight
                />
              </div>
            </>
          )}
        </div>

        {/* ─── Sabit alt footer — adıma göre butonlar değişir ─── */}
        <div className="shrink-0 border-t border-gray-200 bg-white px-5 py-4">
          {step === STEP_RESULT ? (
            <button
              type="button"
              onClick={handleBitti}
              className="w-full py-3 rounded-xl text-base font-SemiBold text-white bg-gradient-to-r from-emerald-600 to-emerald-700 shadow-md hover:opacity-95"
            >
              Tamamlandı
            </button>
          ) : (
            <div className="flex gap-3">
              {/* Geri: 1. adımda İptal, kod doğrulandıktan sonra hiç yok */}
              {step === STEP_PRODUCT ? (
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-Medium rounded-xl hover:bg-gray-50 disabled:opacity-50"
                >
                  <FaTimes className="w-3.5 h-3.5" />
                  İptal
                </button>
              ) : step !== STEP_NEW_QUOTA ? (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-Medium rounded-xl hover:bg-gray-50 disabled:opacity-50"
                >
                  <FaArrowLeft className="w-3.5 h-3.5" />
                  Geri
                </button>
              ) : null}

              {step === STEP_CODE ? (
                <button
                  type="button"
                  onClick={handleKoduDogrula}
                  disabled={isLoading || !code.trim()}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-SemiBold text-white bg-gradient-to-r from-violet-600 to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Kodu Doğrula
                </button>
              ) : step === STEP_NEW_QUOTA ? (
                <button
                  type="button"
                  onClick={handleKodUret}
                  disabled={isLoading || newQuota < 0}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-SemiBold text-white bg-gradient-to-r from-violet-600 to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Kod Üret
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={isLoading}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-SemiBold text-white bg-gradient-to-r from-violet-600 to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Devam
                  <FaArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* ─── Basarisiz sonuc overlay — Tamam ile dismiss, adım açık kalır ─── */}
        {!isFinished && (qrError || quotaError) && (
          <div className="absolute inset-0 z-20 flex flex-col bg-white/95 backdrop-blur-sm">
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-6 px-6">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center animate-[pulse_2s_ease-in-out_infinite]">
                <FaTimesCircle className="w-12 h-12 text-red-600" />
              </div>
              <div>
                <p className="text-base font-SemiBold text-gray-800 mb-2">İşlem Başarısız</p>
                <p className="text-sm text-gray-600 max-w-xs">
                  {qrError &&
                    (qrErrorData?.data?.message ||
                      "Girilen Güvenlik Kodu veya makine değerleri hatalı.")}
                  {quotaError &&
                    (quotaErrorData?.data?.message ||
                      "Kota güncellenemedi. Girilen değerleri kontrol edin.")}
                </p>
                {qrError && (
                  <p className="text-xs text-gray-500 max-w-xs mt-3">
                    Makine ekranındaki <span className="font-SemiBold">Toplam Sayaç</span>,{" "}
                    <span className="font-SemiBold">Kalan Kota</span> ve{" "}
                    <span className="font-SemiBold">Güvenlik Kodu</span> değerlerini tekrar
                    kontrol edin — üçü de birebir aynı olmalıdır.
                  </p>
                )}
              </div>
            </div>
            <div className="p-5 flex gap-3">
              {qrError && (
                <button
                  type="button"
                  onClick={() => { resetQR(); setStep(STEP_MACHINE); }}
                  className="flex-1 py-3 rounded-xl text-base font-SemiBold text-white bg-gradient-to-r from-violet-600 to-purple-700 shadow-md hover:opacity-95"
                >
                  Değerleri Düzelt
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (qrError) resetQR();
                  if (quotaError) resetQuota();
                }}
                className={`py-3 rounded-xl text-base font-SemiBold shadow-md hover:opacity-95 ${
                  qrError
                    ? "px-5 bg-white border border-gray-200 text-gray-600"
                    : "w-full text-white bg-gradient-to-r from-gray-600 to-gray-700"
                }`}
              >
                Tamam
              </button>
            </div>
          </div>
        )}
      </div>

      {mounted && (
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
          // Boş alana tıklayınca kapanmasın — kullanıcı Tamamlandı veya İptal/X
          // ile çıkmalı.
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default ManualQuotaDrawer;
